import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import moment from "moment";
import {
  FacilityInformation,
  FacilityProcessDetails,
  FacilityState,
} from "../../../sc-models/facility-product-dto";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import { FormConfigProps } from "../../../../../../shared/components/form-builder/form-builder.model";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { buyerSellerDetailsService } from "../../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { useScStore } from "../../../../../../store/scStandard.store";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { 
  FARM_CODES, 
  SUPPLY_CHAIN_CODES, 
  EXEMPT_COMBO_CODES, 
  ALL_FARM_CATEGORIES,
  FarmCategoryUtils 
} from "../../../constants/farm-categories";

interface FacilityInfoFormProps {
  formRef: any;
  facilityData: FacilityInformation;
  setFacilityData: React.Dispatch<React.SetStateAction<FacilityInformation>>;
  mfExists?: boolean;
  mainSite?: FacilityState;
  setSubsequentFacilityFlag: React.Dispatch<React.SetStateAction<boolean>>;
  subsequentFacilityFlag: boolean;
  setScSubsequentFacilityButtonFlag: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  /** True when the drawer was opened via the Edit action (not Add new) */
  isEditing?: boolean;
  processCodes: string[];
  // setIsFarmSite: React.Dispatch<React.SetStateAction<boolean>>;
  // scNoOfFarmsFlag: boolean;
  // setScNoOfFarmsFlag: React.Dispatch<React.SetStateAction<boolean>>;
  scSubsequentFacilityButtonFlag: boolean;
  facilityInfoData: FacilityProcessDetails[];
}

const FacilityInfoForm: React.FC<FacilityInfoFormProps> = ({
  formRef,
  facilityData,
  setFacilityData,
  mainSite,
  mfExists,
  setSubsequentFacilityFlag,
  subsequentFacilityFlag,
  setScSubsequentFacilityButtonFlag,
  isEditing,
  processCodes,
  // setIsFarmSite,
  // scNoOfFarmsFlag,
  // setScNoOfFarmsFlag,
  scSubsequentFacilityButtonFlag,
  facilityInfoData,
}) => {
  // Lock Facility Type, TE-ID, and Name when editing an existing MF facility
  const isMfEdit = Boolean(isEditing && facilityData?.facilityType === 'MF');

  /**
   * Returns true if the value is an NA-like variant that should be auto-converted to "na".
   * Handles: NA, N/A, N.A, N-A, N A (any case/separator), "not available",
   * "not applicable", "null", "nil", "none", or any word starting with "not".
   */
  const isNaLike = (value: any): boolean => {
    if (!value || typeof value !== 'string') return false;
    const trimmed = value.trim();
    // Regex: matches all N?A variants regardless of case/separator (na, n/a, n.a, n-a, n a…)
    if (/^n\s*[\/\-\.]?\s*a\.?$/i.test(trimmed)) return true;
    const lower = trimmed.toLowerCase();
    return ['not available', 'not applicable', 'null', 'nil', 'none'].includes(lower);
  };

  /** Shared onChange handler for text fields that must auto-convert NA-like input to 'na'. */
  const naConvertOnChange = (
    value: any,
    _formValues: any,
    setFieldValue: (field: string, val: any) => void,
    fieldName: string
  ) => {
    if (typeof value !== 'string') return;
    const firstWord = value.trim().split(/\s+/)[0]?.toLowerCase();
    if (isNaLike(value) || firstWord === 'not') {
      setFieldValue(fieldName, 'na');
    }
  };
  const farmCodes = FarmCategoryUtils.getFarmCodesSet();
  const supplyChainCodes = FarmCategoryUtils.getSupplyChainCodesSet();
  const exemptComboCodes = FarmCategoryUtils.getExemptComboCodesSet();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { SC_ID } = useParams();
  const { executeGet } = useApi();
  const [cbLicensingCodes, setCBLicensingCodes] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const { setCountries, setStates, setCBLicensingsCodes } =
    useTransactionCertificateStore();
  const [csCbCode, setCsCbCode] = useState();
  // const [state, setState] = useState([]);

  const [visible, setVisible] = useState({
    iId: true,
    name: true,
    iIdLabel:
      facilityData?.facilityTeId && facilityData?.facilityTeId !== ""
        ? false
        : true,
  });
  const [isProdCapacityRequired, setIsProdCapRequired] =
    useState<boolean>(false);
  const [isFarmNumRequired, setIsFarmNumRequired] = useState<boolean>(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [facilityTypeOptions, setFacilityTypeOptions] = useState([]);
  const [facilityCbOptions, setFacilityCbOptions] = useState([]);
  const [facilityStandardOptions, setFacilityStandardOptions] = useState([]);
  // Read SC standards from the Zustand store — updated live by standard-details-form.tsx
  // whenever the user adds/removes a standard in the Standard Information tab.
  const { scStandards } = useScStore();
  const [disable, setDisable] = useState({
    country: true,
    state: true,
    town: true,
    postcode: true,
    address1: true,
  });
  const [scValidFromDate, setScValidFromDate] = useState();
  const { notify } = useNotification();

  const getCountry = async () => {
    try {
      const result = await dtrackItServiceV4.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setCountryOptions(countries);
      setCountries(countries);
    } catch (err) {
      notify("Failed to fetch countries", "error");
    }
  };
  const updateReqFields = (codes: string[]) => {
    const codeSet = new Set(codes);

    // Helper: check if all codes are in a given set
    const isSubsetOf = (targetSet: Set<string>) =>
      [...codeSet].every((code) => targetSet.has(code));

    // Case 1: Only farm codes → optional
    if (isSubsetOf(farmCodes)) return false;

    // Case 2: Only supply chain codes → optional
    if (isSubsetOf(supplyChainCodes)) return false;

    // Case 3: Only exempt combo codes → optional
    if (isSubsetOf(exemptComboCodes)) return false;

    // Case 4: Farm codes + any other code (except exempt ones) → mandatory
    const hasFarmCode = [...codeSet].some((code) => farmCodes.has(code));
    const hasNonExemptOtherCode = [...codeSet].some(
      (code) => !farmCodes.has(code) && !exemptComboCodes.has(code)
    );

    if (hasFarmCode && hasNonExemptOtherCode) return true;

    // Default: mandatory
    return false;
  };
  function computeFarmNumRequired(codes: string[]): boolean {
    const farmCodes = FarmCategoryUtils.getFarmCodesSet();
    const supplyChainCodes = FarmCategoryUtils.getSupplyChainCodesSet();

    const codeSet = new Set(codes);
    const hasFarmCode = codes.some((code) => farmCodes.has(code));

    if (!hasFarmCode) return false;

    const nonFarmCodes = codes.filter((code) => !farmCodes.has(code));

    // Case 1: Only farm codes
    if (nonFarmCodes.length === 0) return true;

    // Case 2: Farm + supply chain codes only
    const allNonFarmAreSupplyChain = nonFarmCodes.every((code) =>
      supplyChainCodes.has(code)
    );
    if (allNonFarmAreSupplyChain) return true;

    // Case 3: Farm + other codes (excluding supply chain)
    const hasOtherNonSupplyChain = nonFarmCodes.some(
      (code) => !supplyChainCodes.has(code)
    );
    if (hasOtherNonSupplyChain) return true;

    return false;
  }
  const getState = async (countryCode: string) => {
    try {
      setIsLoadingStates(true);
      const result = await dtrackItServiceV4.getStateBasedonCountry(
        countryCode
      );
      const states = result.data
        .filter((t: any) => !countryCode.includes(t.code_1)) // Do not include the country itself as a state
        .map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
      setStateOptions(states);
      setStates(states);
    } catch (err) {
      setStateOptions([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  // Commented out hardcoded options - now using API calls
  // const facilityStandardCodes: OptionType[] = [
  //   { label: "Organic Content Standard (OCS)", value: "OCS" }, // Represents "Valid"
  //   { label: "Global Recycled Standard (GRS)", value: "GRS" }, // Represents "Expired"
  //   { label: "Recycled Claim Standard (RCS)", value: "RCS" }, // Represents "Suspended"
  //   { label: "Responsible Down Standard (RDS)", value: "RDS" }, // Represents "Withdrawn"
  //   { label: "Responsible Wool Standard (RWS)", value: "RWS" }, // Represents "Withdrawn"
  //   { label: "Responsible Mohair Standard (RMS)", value: "RMS" }, // Represents "Withdrawn"
  //   { label: "Responsible Alpaca Standard (RAS)", value: "RAS" }, // Represents "Withdrawn"
  //   { label: "Content Claim Standard (CCS)", value: "CCS" }, // Represents "Withdrawn"
  //   // { label: "SFA ", value: "SFA" }, // Represents "Withdrawn"
  //   // { label: "Global Organic Textile Standard (GOTS)", value: "GOTS" }, // Represents "Withdrawn"
  // ];
  const fetchTEDetails = async () => {
    const response: any = await executeGet(API_ROUTES.GET_CO_DETAILS_V4, {
      scId: SC_ID,
    });
    const mappedDetails = {
      account_name: response.data.certifiedOrganizationName,
      TE_ID: response.data.certifiedOrganizationTeId,
      street: response.data.certifiedOrganizationStreetAddress,
      city: response.data.certifiedOrganizationTown,
      postal_code: response.data.certifiedOrganizationPostcode,
      country: response.data.certifiedOrganizationCountryOrArea,
      state: response.data.certifiedOrganizationStateOrProvince,
    };
    // Move this check here to ensure it happens after data is loaded
    if (response?.certifiedOrganizationTeId) {
      setVisible((prev) => ({
        ...prev,
        iIdLabel: false,
      }));
    }
    return mappedDetails;
  };

  const fetchSCStandardDetails = async () => {
    const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, {
      scId: SC_ID,
    });
    if (result?.data?.scStandardInformation?.length > 0) {
      // NOTE: Do NOT overwrite the Zustand store here.
      // The store is seeded by scope-certificate-details/index.tsx on load and kept live
      // by standard-details-form.tsx on every add/remove. Overwriting here would replace
      // the user's current UI selection with stale backend data.
      setScValidFromDate(result?.data?.scValidFromDate);
      // Auto-set facility added date to SC valid from date if not already set
      if(!facilityData?.facilityAddedDate || facilityData?.facilityAddedDate==="") {
        formRef?.current?.setFieldValue(
          "facilityAddedDate",
          result?.data?.scValidFromDate
        );
      }
      // Auto-set facility standard from the STORE (live selection), not from the API response
      if (!facilityData?.facilityStandard || facilityData?.facilityStandard.length === 0) {
        const liveStandards = useScStore.getState().scStandards;
        formRef?.current?.setFieldValue("facilityStandard", liveStandards.length > 0 ? liveStandards : result?.data.scStandardInformation.map((d: any) => d.scStandard));
      }
      const scNoOfFarms = result?.data.scStandardInformation.filter(
        (detail: any) => detail.scNumberOfFarms > 0
      );
      // setScNoOfFarmsFlag(scNoOfFarms.length > 0);
      checkInitialValidations(scNoOfFarms.length > 0);
    }
    return result;
  };

  // Auto-select facility standards based on facility type using SC standards from store
  const autoSelectFacilityStandards = (facilityType: string) => {
    if (scStandards.length === 0) return; // Wait for SC standards to be loaded

    if (facilityType === "MF") {
      // For MF: facility standard is non-editable and ALWAYS equals the SC standards.
      // Always sync from the live store — never keep stale backend data here.
      formRef?.current?.setFieldValue("facilityStandard", scStandards);
    } else if (["FA", "AS", "CS"].includes(facilityType)) {
      // For FA, AS, CS: user can customize — only auto-fill when no value saved yet.
      if (!facilityData?.facilityStandard || facilityData.facilityStandard.length === 0) {
        formRef?.current?.setFieldValue("facilityStandard", scStandards);
      }
    }
  };

  const fetchTEDetailsForMF = async () => {
    const details = await fetchTEDetails();
    handleSellerTEIdValueChange(details);
  };

  useEffect(() => {
    const getCBLicensingCodes = async () => {
      const result: any = await dtrackItServiceV4.getCBLicensingCodes();
      // Utills.cl(`result getCBLicensingCodes ${JSON.stringify(result)}`)
      const cbLicensing = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1}  `,
      }));
      // Utills.cl(`cbLicensing ${JSON.stringify(cbLicensing)}`)
      setCBLicensingCodes(cbLicensing);
      setCBLicensingsCodes(cbLicensing);
    };

    const getFacilityType = async () => {
      try {
        const result: any = await dtrackItServiceV4.getFacilityType();
        const facilityTypes = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setFacilityTypeOptions(facilityTypes);
      } catch (err) {
        notify("Failed to fetch facility types", "error");
      }
    };

    const getFacilityCbOptions = async () => {
      try {
        const result: any =
          await dtrackItServiceV4.getFacilityCbOfIndependentlyCertifiedSubcontractor();
        const facilityCbs = result.data.map((t: any) => ({
          value: t.code_1,
          label: t.code_1,
        }));
        setFacilityCbOptions(facilityCbs);
      } catch (err) {
        notify("Failed to fetch facility CB options", "error");
      }
    };

    const getFacilityStandards = async () => {
      try {
        const result: any = await dtrackItServiceV4.getScStandardValues();
        const facilityStandards = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setFacilityStandardOptions(facilityStandards);
      } catch (err) {
        notify("Failed to fetch facility standards", "error");
      }
    };

    getCBLicensingCodes();
    getFacilityType();
    getFacilityCbOptions();
    getFacilityStandards();

    if (!mfExists && facilityData?.facilityType === "MF") {
      fetchTEDetailsForMF();
    }
  }, []);

  useEffect(() => {
    if (facilityData?.facilityType === "MF") {
      setShowWarning(true);
    }
  }, [facilityData]);
  useEffect(() => {
    setIsProdCapRequired(updateReqFields(processCodes));
    setIsFarmNumRequired(computeFarmNumRequired(processCodes));
  }, [processCodes]);

  // Auto-set facility added date when scValidFromDate changes
  useEffect(() => {
    if (scValidFromDate && formRef?.current && (!facilityData?.facilityAddedDate || facilityData?.facilityAddedDate === "")) {
      formRef.current.setFieldValue('facilityAddedDate', scValidFromDate);
    }
  }, [scValidFromDate, facilityData?.facilityAddedDate]);
  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      Utills.cl(
        `fetchTEDetallsFromAPI details, query ${JSON.stringify(
          queryParams
        )} response data ${JSON.stringify(data)}`
      );

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error;
    }
  };
  const fetchCbCodeForTEId = async(teId:string)=>{
    const cbDetails = await executeGet(API_ROUTES.GET_CB_CODE_FOR_TE_ID,{teId});
    const cbCode = cbDetails && cbDetails?.data?.response[0]?cbDetails?.data?.response[0]?.md_source_system_identifier:"";
    setCsCbCode(cbCode);
    formRef.current.setFieldValue("facilityCbOfIndependentlyCertifiedSubcontractor", cbCode);
  }
  const handleSellerTEIdValueChange = (value: any) => {
    const fields = [
      "address1",
      "town",
      "postcode",
      "stateProvince",
      "countryArea",
      "name",
      "iId",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setDisable((prev) => ({
        ...prev,
        country: true,
        state: true,
      }));
      setVisible((prev) => ({
        ...prev,
        // name: true,
        iIdLabel: true,
      }));
    } else {
      formRef.current.setFieldValue("facilityName", value.account_name || "");
      formRef.current.setFieldValue("facilityTeId", value.TE_ID);
      if(formRef?.current?.values?.facilityType==='CS'){
        fetchCbCodeForTEId(value?.TE_ID);
      }
      // formRef.current.setFieldValue("address1", value.street);

      //handle address1
      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("facilityAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue("facilityAddress1", value.street);
      }

      // setSubsequentFacilityFlag(true);
      // Handle town
      if (value.city != null) {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("facilityTown", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("facilityTown", "");
      }
      // Handle postcode
      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue("facilityPostcode", value.postal_code);
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("facilityPostcode", "");
      }

      // Handle country
      if (value.country) {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
        formRef.current.setFieldValue("facilityCountryOrArea", value.country);
        setDisable((prev) => ({
          ...prev,
          country: true,
          state: false, // Enable state dropdown if only country exists
        }));
      }

      // Handle state separately
      if (value.state) {
        formRef.current.setFieldValue("facilityStateOrProvince", value.state);
        setDisable((prev) => ({
          ...prev,
          state: true,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          state: false, // Enable state dropdown if state is not provided
        }));
      }

      setVisible((prev) => ({
        ...prev,
        // name: false,
        iIdLabel: value?.TE_ID && value?.TE_ID !== "" ? false : true,
      }));
    }
    // Re-run validation after all programmatic setFieldValue calls have settled.
    // formRef.current.setFieldValue bypasses FormBuilderV4's overridden version
    // (which passes shouldValidate=true), so errors from a prior Next-click
    // would otherwise linger even after the fields are auto-filled.
    setTimeout(() => { formRef.current?.validateForm(); }, 0);
  };
  useEffect(() => {
    if (facilityData?.facilityCountryOrArea) {
      let countryCode = JSON.stringify(
        `[${[facilityData.facilityCountryOrArea].map((item) => `'${item}'`)}]`
      ).replace(/^"|"$/g, "");
      getState(countryCode);
    }
  }, [facilityData?.facilityCountryOrArea]);

  useEffect(() => {
    if (SC_ID) {
      fetchSCStandardDetails();
    }

    getCountry();
  }, []);

  // Auto-select facility standards when component loads with existing facility data
  useEffect(() => {
    if (facilityData?.facilityType && scStandards.length > 0) {
      autoSelectFacilityStandards(facilityData.facilityType);
    }
  }, [facilityData?.facilityType, scStandards]);

  const checkInitialValidations = (scFarmsFlag: boolean) => {
    const selectedProcesses = facilityInfoData.filter((process) =>
      FarmCategoryUtils.isFarmCategory(process.facilityProcessCategoryCode)
    );
    if (
      facilityData?.facilityType !== "MF" &&
      // scFarmsFlag &&
      selectedProcesses.length > 0
    ) {
      setSubsequentFacilityFlag(true);
      setVisible((prev) => ({
        ...prev,
        name: false,
      }));
      setDisable((prev) => ({
        ...prev,
        postcode: false,
        town: false,
        country: false,
        state: false,
        address1: false,
      }));
    } else {
      setSubsequentFacilityFlag(false);
    }
  };

  const handleSellerNameValueChange = (value: any) => {
    const fields = [
      "address1",
      "town",
      "postcode",
      "stateProvince",
      "countryArea",
      "name",
      "iId",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });

      setVisible((prev) => ({
        ...prev,
        iId: true,
      }));
    } else {
      formRef.current.setFieldValue("facilityName", value.account_name);
      formRef.current.setFieldValue("facilityTeId", value.TE_ID);
      if(formRef?.current?.values?.facilityType==='CS'){
        fetchCbCodeForTEId(value?.TE_ID);
      }
      // formRef.current.setFieldValue("address1", value.street);

      //handle address1
      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("facilityAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue("facilityAddress1", value.street);
      }
      // Handle town
      if (value.city != null) {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("facilityTown", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("facilityTown", "");
      }
      // Handle postcode
      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue("facilityPostcode", value.postal_code);
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("facilityPostcode", "");
      }
      formRef.current.setFieldValue(
        "facilityCountryOrArea",
        value?.country === null ? "" : value?.country
      );
      setDisable((prev) => ({
        ...prev,
        country: value?.country != null ? true : false,
      }));
      formRef.current.setFieldValue(
        "facilityStateOrProvince",
        value.state == null ? "" : value.state
      );
      setVisible((prev) => ({
        ...prev,
        iId: false,
      }));
    }
    // Re-run validation after all programmatic setFieldValue calls have settled.
    setTimeout(() => { formRef.current?.validateForm(); }, 0);
  };

  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setFacilityData(facilityData);
  };

  const formConfig: FormConfigProps = {
    pageName: "sc-facilitydetails",
    initialValues: facilityData,
    validationSchema: Yup.object().shape({
      facilityTeId: Yup.string()
        .max(11, "Max 11 characters")
        .matches(/^TE-\d{8}$/, "Format TE-########")
        .when("facilityType", {
          is: (value: string) => subsequentFacilityFlag,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required("TE-ID is required"),
        }),
      // facilityNo: Yup.string().required("Facility No. is required"),
      ...(mfExists == true
        ? {
            facilityType: Yup.string()
              .test(
                "is-mf",
                "Main Facility already exists. Choose a different facility type.",
                (value: string | undefined, context: Yup.TestContext) => {
                  const teId = context.parent.facilityTeId; // Access another field
                  return !(
                    value === "MF" &&
                    teId !== mainSite?.facilityInfo?.facilityTeId
                  );
                }
              )
              .required("Facility Type is required"),
          }
        : { facilityType: Yup.string().required("Facility Type is required") }),
      // facilityOARId: Yup.string().optional(),
      facilityLastAuditDate: Yup.date().when("facilityType", {
        is: (val: any) => ["MF", "FA", "AS"].includes(val),
        then: (schema) => schema.required("Facility Last Audit Date is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      brandNames: Yup.string(),
      facilityStandard: Yup.array()
        .of(Yup.string())
        .min(1, "At least one Facility Standard must be selected")
        .required("Facility Standard is required"),
      facilityName: Yup.string()
        .required("Name is required")
        .max(100, "Max 100 characters")
        .test('na-only', 'Only lowercase "na" allowed for NA value', (value) => {
          if (!value) return true;
          const lower = value.trim().toLowerCase();
          const naLikes = ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"];
          if (!naLikes.includes(lower)) return true;
          return lower === "na";
        })
        .test('no-whitespace', 'Whitespace-only value is not allowed', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        }),
      facilityAddress1: Yup.string()
        .required("Address 1 is required")
        .max(1000, "Max 1000 characters")
        .test('na-only', 'Only lowercase "na" allowed for NA value', (value) => {
          if (!value) return true;
          const lower = value.trim().toLowerCase();
          const naLikes = ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"];
          if (!naLikes.includes(lower)) return true;
          return lower === "na";
        })
        .test('no-whitespace', 'Whitespace-only value is not allowed', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        }),
      facilityPostcode: Yup.string()
        .required("Postcode is required")
        .max(50, "Max 50 characters")
        .test('na-only', 'Only lowercase "na" allowed for NA value', (value) => {
          if (!value) return true;
          const lower = value.trim().toLowerCase();
          const naLikes = ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"];
          if (!naLikes.includes(lower)) return true;
          return lower === "na";
        })
        .test('no-whitespace', 'Whitespace-only value is not allowed', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        }),
      facilityTown: Yup.string()
        .required("Town is required")
        .max(255, "Max 255 characters")
        .test('na-only', 'Only lowercase "na" allowed for NA value', (value) => {
          if (!value) return true;
          const lower = value.trim().toLowerCase();
          const naLikes = ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"];
          if (!naLikes.includes(lower)) return true;
          return lower === "na";
        })
        .test('no-whitespace', 'Whitespace-only value is not allowed', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        }),
      facilityCountryOrArea: Yup.string().required("Country/Area is required"),
      facilityStateOrProvince: Yup.string().required(
        "State/Province is required"
      ),
      facilityCbOfIndependentlyCertifiedSubcontractor:
        formRef?.cuurent?.values?.facilityType === "CS"
          ? Yup.string().required(
              "Please enter CB of Independently Certified Subcontractor"
            )
            .max(6, "Max 6 characters")
            .matches(/^CB-[A-Z]{3}$/, "Format CB-XXX"):Yup.string().nullable(),
      facilityAdditionalScopes: Yup.string().max(2000, "Max 2000 characters"),
      facilityAddedDate: Yup.date()
        .test(
          "not-added-before-sc-created",
          "Facility Added Date cannot be before SC Valid From Date",
          function (value) {
            if (!value || !scValidFromDate) return true;
            const dateDiff = Utills.calculateDateDifferenceInDays(
              new Date(value),
              new Date(scValidFromDate + "T00:00:00Z")
            );
            return dateDiff <= 0; // Changed from <= 0 to >= 0 to allow same date
          }
        )
        .required(),
      facilityRemovedDate: Yup.date()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null ? null : value
        )
      //   .test(
      //   "facility-removed-sequence",
      //   "Facility Removed Date must be after or equal to Facility Added Date",
      //   function (value) {
      //     const firstCreated = this.parent.facilityAddedDate;
      //     if (!firstCreated || !value) return true;
      //     return new Date(firstCreated) <= new Date(value);
      //   }
      // )
      /***
      As part of SCV4 enhancements, UI and External API
      Added Date > removed date
      Added Date <= removed date

      both from above are allowed hence remove any validation which checks added and removed date for both standard and facility
        */
      ,
      facilityProductionCapacity: Yup.number()
        .nullable()
        .transform((value, originalValue) => 
          originalValue === "" || originalValue === null ? null : value
        )
        .typeError("Facility Production Capacity must be a number")
        .positive("Facility Production Capacity must be a positive number")
        .when([], {
          is: () => isProdCapacityRequired,
          then: (schema) => schema.required("Facility Production Capacity is required"),
          otherwise: (schema) => schema.optional(),
        }),
    }),
    sections: [
      {
        title: "Facility Information",
        fields: [
          // {
          //   label: "Facility No.",
          //   name: "facilityNo",
          //   type: "text",
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: true,
          //   props: { placeholder: "Enter Facility No." },
          // },
          {
            label: "Facility Type",
            name: "facilityType",
            type: "select",
            // Disabled when editing an existing MF — type cannot change on edit
            disabled: (values: any) => isMfEdit || (!mfExists && facilityData?.facilityType === "MF"),
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              // Hide MF from the dropdown when adding a new facility and an MF already exists.
              // On edit the field is disabled anyway, so all options can remain.
              options: (mfExists && !isEditing)
                ? facilityTypeOptions.filter((opt: any) => opt.value !== 'MF')
                : facilityTypeOptions,
              placeholder: "Select Facility Type",
            },
            events: {
              onChange: async (updatedValue: any) => {
                setShowWarning(updatedValue === "MF");
                setVisible((prev) => ({
                  ...prev,
                  iId: false,
                }));
                if (updatedValue === "MF") {
                  const details = await fetchTEDetails();
                  console.log("Mapped Details:", details);
                  handleSellerTEIdValueChange(details);
                } else {
                  setVisible((prev) => ({
                    ...prev,
                    iId: true,
                  }));
                  const mappedDetails = {
                    account_name: "",
                    TE_ID: "",
                    street: "",
                    city: "",
                    postal_code: "",
                    country: "",
                    state: "",
                  };
                  handleSellerTEIdValueChange(mappedDetails);
                }
                if (
                  updatedValue !== "MF"
                  // && scNoOfFarmsFlag
                ) {
                  setSubsequentFacilityFlag(true);
                  setVisible((prev) => ({
                    ...prev,
                    name: false,
                  }));
                  setDisable((prev) => ({
                    ...prev,
                    postcode: false,
                    town: false,
                    country: false,
                    state: false,
                    address1: false,
                  }));
                  formRef.current?.setFieldValue("facilityCountryOrArea", "");
                  formRef.current?.setFieldValue("facilityStateOrProvince", "");
                } else {
                  setSubsequentFacilityFlag(false);
                }
                
                // Auto-select facility standards based on facility type
                autoSelectFacilityStandards(updatedValue);
              },
            },
            ...(mfExists ? {} : { value: "MF" }),
          },
          {
            label: "Facility TE-ID",
            name: "facilityTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true, //(values: any) => !subsequentFacilityFlag,
            value: facilityData.facilityTeId,
            // When editing MF, hide the search variant and show the plain text (disabled) variant
            visible: (value: any) => visible.iId && !isMfEdit,
            props: {
              placeholder: "Search TE-ID",
              variant: "outlined",
              restrictInputFormat: visible?.iIdLabel,
              param: "facilityTeId",
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Facility TE-ID",
            name: "facilityTeId",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            // Always disabled as plain text; also shown when isMfEdit to lock the field
            disabled: true,
            visible: (value: any) => !visible.iId || isMfEdit,
            props: {
              placeholder: "Enter TE-ID",
              fullWidth: true,
            },
          },
          {
            label: "Facility Name",
            name: "facilityName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            value: facilityData.facilityName,
            // When editing MF, hide search variant and show plain text (disabled) variant
            visible: (value: any) => visible.name && !isMfEdit,
            props: {
              placeholder: "Search Name",
              variant: "outlined",
              param: "name",
              onChange: handleSellerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Facility Name",
            name: "facilityName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            // Disabled when editing MF, or when visible.name is true (search variant takes over)
            disabled: (values: any) => isMfEdit || visible.name,
            visible: (value: any) => !visible.name || isMfEdit,
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) =>
                naConvertOnChange(value, formValues, setFieldValue, 'facilityName'),
            },
            props: {
              placeholder: "Enter Name",
              fullWidth: true,
            },
          },

          {
            label: "Facility Street Address",
            name: "facilityAddress1",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (values: any) => disable?.address1,
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) =>
                naConvertOnChange(value, formValues, setFieldValue, 'facilityAddress1'),
            },
            props: { placeholder: "Enter Address 1" },
          },
          {
            label: "Facility Town",
            name: "facilityTown",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (values: any) => disable?.town,
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) =>
                naConvertOnChange(value, formValues, setFieldValue, 'facilityTown'),
            },
            props: { placeholder: "Enter Town" },
          },
          {
            label: "Facility Postcode",
            name: "facilityPostcode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (values: any) => disable?.postcode,
            helperText: 'Please fill "na" if postcode is not applicable',
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) =>
                naConvertOnChange(value, formValues, setFieldValue, 'facilityPostcode'),
            },
            props: { placeholder: "Enter Postcode" },
          },
          {
            label: "Facility Country/Area",
            name: "facilityCountryOrArea",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (values: any) => disable.country,
            events: {
              onChange: (newValue: any) => {
                let countryCode = JSON.stringify(
                  `[${[newValue].map((item) => `'${item}'`)}]`
                ).replace(/^"|"$/g, "");
                getState(countryCode);
                // Clear the state value when country changes to prevent invalid selections
                formRef?.current?.setFieldValue("facilityStateOrProvince", "");
              },
            },
            props: {
              enableSearch: true,
              options: countryOptions,
              placeholder: "Select Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "Facility State/Province",
            name: "facilityStateOrProvince",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (values: any) => disable.state,
            props: {
              options: stateOptions,
              enableSearch: true,
              placeholder: "Select State/Province",
              fullWidth: true,
            },
          },
          // {
          //   label: 'Facility OAR ID',
          //   name: 'facilityOARId',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: { placeholder: 'Enter OAR ID' },
          // },
          {
            label: "Facility Added Date",
            name: "facilityAddedDate",
            type: "date",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                if (newValue && scValidFromDate && formRef?.current) {
                  const facilityAddedDate = new Date(newValue);
                  const scValidFrom = new Date(scValidFromDate + "T00:00:00Z");
                  
                  // Check if facility added date is the same as SC valid from date
                  const isSameDate = facilityAddedDate.toDateString() === scValidFrom.toDateString();
                  
                  if (isSameDate) {
                    // Auto-set to SC valid from date but keep field editable
                    formRef.current.setFieldValue('facilityAddedDate', scValidFromDate);
                  }
                }
              },
            },
            props: (values: any) => {
              const moment = require('moment');
              return { 
                placeholder: "Select Added Date", 
                disableFuture: false,
                minDate: scValidFromDate ? moment(scValidFromDate + "T00:00:00Z") : undefined
              };
            },
          },
          {
            label: "Facility Removed Date",
            name: "facilityRemovedDate",
            type: "date",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: (values: any) => {
              return { 
                placeholder: "Select Removed Date", 
                disableFuture: false,
              };
            },
          },
          {
            label: "Facility Not Audited",
            name: "facilityNotAudited",
            type: "checkbox",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            events: {
              // form-builder-v4 extracts e.target.checked before calling onChange,
              // so the first argument is already the boolean value — not the DOM event.
              onChange: (checked: boolean, formValues: any, setFieldValue: any) => {
                if (checked) {
                  setFieldValue("facilityLastAuditDate", "1900-01-01");
                } else {
                  setFieldValue("facilityLastAuditDate", "");
                }
                setFieldValue("facilityNotAudited", checked);
              }
            }
          },
          {
            label: "Facility Last Audit Date",
            name: "facilityLastAuditDate",
            type: "date",
            gridSize: { xs: 12, md: 6, lg: 4 },
            disabled: (values: any) => values.facilityNotAudited,
            required: (values: any) => ["MF", "FA", "AS"].includes(values.facilityType),
            props: { placeholder: "Select Last Audit Date",
              minDate: moment('1900-01-01') // January is month 0
             },
          },
          {
            label: "Facility Standard",
            name: "facilityStandard",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            disabled: (values: any) => values.facilityType === "MF",
            required: true,
            props: (values: any) => {
              // Filter facility standard options to only show SC standards
              const scStandardOptions = facilityStandardOptions.filter((option: any) => 
                scStandards.includes(option.value)
              );
              
              return {
                multiple: true,
                options: scStandardOptions,
                placeholder: "Select Facility Standard",
              };
            },
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                // For FA, AS, CS: Ensure at least one standard is selected
                if (["FA", "AS", "CS"].includes(updatedValues.facilityType)) {
                  if (!newValue || newValue.length === 0) {
                    // If user tries to deselect all, show validation error
                    formRef?.current?.setFieldError("facilityStandard", "At least one Facility Standard must be selected");
                  } else {
                    // Clear any existing error
                    formRef?.current?.setFieldError("facilityStandard", undefined);
                  }
                }
              },
            },
          },
          {
            label: "Facility OS ID",
            name: "facilityOsId",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: { placeholder: "Enter OS ID" },
          },
          {
            label: "Facility Additional Scopes",
            name: "facilityAdditionalScopes",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: { placeholder: "Enter Additional Scopes" },
          },
          // {
          //   label: "Subcontractor License No.",
          //   name: "subcontractorLicenseNo",
          //   type: "text",
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   disabled: (values: any) => values?.facilityType !== "CS",
          //   required: (values: any) => {
          //     return values.facilityType == "CS" ? true : false;
          //   },
          //   visible: (values: any) => values.facilityType === "CS",
          //   props: { placeholder: "Enter Subcontractor License No." },
          // },
          {
            label: "Facility Production Capacity",
            // visible:(values)=>scNoOfFarmsFlag && (values?.facilityType === "MF" || values?.facilityType === "FA"),
            visible: () => true,
            name: "facilityProductionCapacity",
            type: "number",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: isProdCapacityRequired,
            props: { 
              placeholder: "Enter Facility Production Capacity",
              inputProps: { 
                step: "0.01",
                min: "0"
              },
              onInput: (e: any) => {
                // Allow only numbers and decimal point
                let value = e.target.value.replace(/[^0-9.]/g, '');
                
                // Ensure only one decimal point
                const parts = value.split('.');
                if (parts.length > 2) {
                  value = parts[0] + '.' + parts.slice(1).join('');
                }
                
                // Limit to 2 decimal places
                if (parts.length === 2 && parts[1].length > 2) {
                  value = parts[0] + '.' + parts[1].substring(0, 2);
                }
                
                e.target.value = value;
              }
            },
          },

          {
            label: "CB of Independently Certified Subcontractor",
            name: "facilityCbOfIndependentlyCertifiedSubcontractor",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: (values: any) => values?.facilityType === "CS",
            disabled: !!csCbCode,
            visible: (values: any) => values.facilityType === "CS",
            props: {
              options: facilityCbOptions,
              placeholder: "Select Certification Body",
            },
          },
          // {
          //   label: "Expiry Date of Independently Certified Subcontractor",
          //   name: "expiryDate",
          //   type: "date",
          //   gridSize: { xs: 12, md: 6, lg: 6 },
          //   disabled: false,
          //   required: false,
          //   visible: (values: any) => values.facilityType === "CS",
          //   props: { placeholder: "Select Expiry Date", disableFuture: false },
          // },
        ],
      },
    ],
    handleSubmit: (values) => {
      setFacilityData(values); // Assumes `setFacilityData` is defined elsewhere
      toggleDrawer(); // Assumes `toggleDrawer` is defined elsewhere
    },
  };

  return (
    <>
      {showWarning && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#FFF3E0",
            borderRadius: 1,
            border: "1px solid #FFB74D",
          }}
        >
          <Typography color="warning.dark">
            Note: Any changes to the Certified Organization (CO) details should
            be made in the "Certified Organization Details" tab.
          </Typography>
        </Box>
      )}
      <FormBuilderV4
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </>
  );
};

export default FacilityInfoForm;
